import {RepoSelectPanel} from '@github-ui/copilot-chat/components/RepoSelectPanel'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useLastUsedRepository} from '@github-ui/copilot-chat/hooks/use-last-used-repository'
import {isFileReference} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {CopilotChatRepo} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {RefSelector} from '@github-ui/ref-selector'
import {ArrowUpRightIcon, ChevronDownIcon, CommentIcon, SidebarCollapseIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import type {PreviewableContent} from '../ContentPreview/content-preview-types'
import {useContentPreview} from '../ContentPreview/ContentPreviewContext'
import {FileTab} from './FileTab'
import styles from './ResourcePanel.module.css'

interface ResourcePanelProps {
  className?: string
}

export function ResourcePanel({className}: ResourcePanelProps) {
  const {closeResourcePanel, setResourcePanelExpandedPath} = useContentPreview()
  const [repoPanelOpen, setRepoPanelOpen] = useState(false)
  const {fetchError, loading, selectedBranch, selectedRepo, setSelectedBranch, setSelectedRepo} = useSelectedRepo()
  const slimLineTopTabs = copilotFeatureFlags.copilotWorkbenchSlimLineTopTabs

  const repoPanelAnchorRef = useRef<HTMLButtonElement>(null)
  const repoName = selectedRepo ? `${selectedRepo.ownerLogin}/${selectedRepo.name}` : 'Select Repo'

  return (
    <div
      className={clsx(styles.resourcePanel, {[styles.slimLineTopTabs]: slimLineTopTabs}, className)}
      data-testid="resource-panel"
    >
      <div className={styles.resourcePanelHeader}>
        <div className={styles.resourcePanelHeaderRow}>
          <Button
            ref={repoPanelAnchorRef}
            onClick={() => setRepoPanelOpen(true)}
            variant="invisible"
            trailingVisual={ChevronDownIcon}
            className={styles.resourcePanelRepoButton}
          >
            {repoName}
          </Button>
          <RepoSelectPanel
            selectionVariant="instant"
            open={repoPanelOpen}
            onOpenChange={setRepoPanelOpen}
            selectedRepoIds={new Set()}
            // eslint-disable-next-line @typescript-eslint/require-await
            onSelectRepo={async (repoId: number) => {
              if (!repoId) return
              setSelectedRepo(repoId)
              setResourcePanelExpandedPath('')
            }}
            submitReturnFocusRef={repoPanelAnchorRef}
            cancelReturnFocusRef={repoPanelAnchorRef}
            anchorRef={repoPanelAnchorRef}
            includeForks
          />
          <div className="d-flex flex-justify-end">
            <IconButton
              size="small"
              icon={SidebarCollapseIcon}
              aria-label="Close resource panel"
              onClick={closeResourcePanel}
              variant="invisible"
            />
          </div>
        </div>
        <div className={styles.resourcePanelHeaderRow}>
          {selectedRepo && (
            <RefSelector
              cacheKey="anything"
              canCreate={false}
              closeOnSelect
              currentCommitish={selectedBranch ?? selectedRepo.defaultBranch}
              defaultBranch={selectedRepo.defaultBranch}
              onSelectItem={ref => {
                setSelectedBranch(ref)
                setResourcePanelExpandedPath('')
              }}
              owner={selectedRepo.ownerLogin}
              repo={selectedRepo.name}
              selectedRefType="branch"
            />
          )}
        </div>
      </div>
      <div className={styles.resourcePanelContent}>
        <FileTab
          fetchError={fetchError}
          loading={loading}
          selectedBranch={selectedBranch}
          selectedRepo={selectedRepo}
        />
      </div>
      <div className={styles.resourcePanelFooter}>
        <ActionMenu>
          <ActionMenu.Button variant="invisible">
            <Label variant="success">Preview</Label>
          </ActionMenu.Button>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.LinkItem
                href="https://github.com/orgs/community/discussions/189235"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
                <ActionList.TrailingVisual>
                  <ArrowUpRightIcon />
                </ActionList.TrailingVisual>
              </ActionList.LinkItem>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </div>
  )
}

function repoFromPreviewableContent(item: PreviewableContent | undefined): {repoID: number; ref?: string} | null {
  if (item?.type === 'file' && isFileReference(item.reference)) {
    return {repoID: item.reference.repoID, ref: item.reference.ref || undefined}
  }
  return null
}

function useSelectedRepo() {
  const manager = useChatManager()
  const {items, selectedItem, itemSelectionVersion, resourcePanelRepoId, setResourcePanelRepoId} = useContentPreview()
  const [lastUsedRepository, setLastUsedRepository] = useLastUsedRepository()
  const [fetchError, setFetchError] = useState<string>('')
  const [selectedRepo, setSelectedRepo] = useState<{
    branch: string | undefined
    repo: CopilotChatRepo | null
    repoID: number
  }>(() => {
    const selected = selectedItem && items.get(selectedItem)
    const info = repoFromPreviewableContent(selected)
    return info
      ? {repo: null, repoID: info.repoID, branch: info.ref}
      : lastUsedRepository
        ? {repo: lastUsedRepository, repoID: lastUsedRepository.id, branch: lastUsedRepository.defaultBranch} // TODO: extend lastUsedRepository to store last used branch
        : {repo: null, repoID: -1, branch: undefined}
  })

  // Track the current repoID via a ref so the effect below doesn't re-fire
  // when the user manually switches repos — only when the selected item changes.
  const selectedRepoIDRef = useRef(selectedRepo.repoID)

  const selectRepo = useCallback(
    (repoID: number, branch?: string) => {
      selectedRepoIDRef.current = repoID
      // if we have the repo cached, use it so there's no flicker while loading
      const cached = manager.service.getCachedRepo(repoID)
      if (cached) {
        setSelectedRepo({repo: cached, repoID, branch: branch ?? cached.defaultBranch})
        setLastUsedRepository(cached)
      } else {
        setSelectedRepo({repo: null, repoID, branch})
      }
    },
    [manager.service, setLastUsedRepository],
  )

  const selectBranch = useCallback((branch: string) => {
    setSelectedRepo(prev => ({...prev, branch}))
  }, [])

  // if the selected item or items map changes and the selected item is associated with a different repo, update selectedRepo
  // NOTE: using a useEffect (rather than a render-time check) ensures this runs even when items is populated
  // after selectedItem is already set (avoiding a race condition where selectedItem changes before items contains it)
  useEffect(() => {
    if (!selectedItem) return
    const selected = items.get(selectedItem)
    const info = repoFromPreviewableContent(selected)
    if (!info) return
    if (info.repoID !== selectedRepoIDRef.current) {
      selectRepo(info.repoID, info.ref)
    } else if (info.ref) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      selectBranch(info.ref)
    }
  }, [items, selectedItem, selectRepo, selectBranch, itemSelectionVersion])

  // React to repo selection requests from clicking a repository reference pill
  useEffect(() => {
    if (resourcePanelRepoId) {
      const {repoId, ref} = resourcePanelRepoId
      // eslint-disable-next-line react-hooks/set-state-in-effect
      selectRepo(repoId, ref)
      setResourcePanelRepoId(null)
    }
  }, [resourcePanelRepoId, selectRepo, setResourcePanelRepoId])

  useEffect(() => {
    // fetch repo if we have an id but no repo
    const fetchRepo = async (repoId: number) => {
      const result = await manager.service.fetchRepo(repoId)
      if (result.ok && result.payload) {
        setSelectedRepo(sr => ({
          repo: result.payload,
          repoID: result.payload.id,
          branch: sr.branch ?? result.payload.defaultBranch,
        }))
        setLastUsedRepository(result.payload)
      } else if (result.error) {
        setFetchError(result.error)
      }
    }
    if (selectedRepo.repoID > 0 && !selectedRepo.repo) {
      void fetchRepo(selectedRepo.repoID)
    }
  }, [manager, selectedRepo, setLastUsedRepository])

  return {
    fetchError,
    loading: selectedRepo.repoID > 0 && !selectedRepo.repo,
    selectedBranch: selectedRepo.branch,
    selectedRepo: selectedRepo.repo,
    setSelectedBranch: selectBranch,
    setSelectedRepo: selectRepo,
  }
}
